/**
 * useActorSync Hook
 * 
 * Synchronizes activeActor from backend when user is authenticated but activeActor is missing
 * This handles cases where:
 * 1. User logged in before actor architecture was fully implemented
 * 2. LocalStorage was cleared but cookies still valid
 * 3. App state got out of sync
 */

import { useEffect, useRef } from 'react';
import { useAuthStore } from '@shared/lib/store/auth-store';
import { getUserActors } from '@/src/entities/user/api/actors.api';

export function useActorSync() {
  const { user, isAuthenticated, activeActor, setActiveActor, setAvailableActors } = useAuthStore();
  const hasSyncedRef = useRef(false);

  useEffect(() => {
    // Only sync if:
    // 1. User is authenticated
    // 2. We have user data
    // 3. But missing activeActor
    // 4. Haven't synced yet this session
    if (!isAuthenticated || !user || activeActor || hasSyncedRef.current) {
      return;
    }

    const syncActiveActor = async () => {
      try {
        console.log('[useActorSync] Syncing activeActor from backend...');
        
        // Fetch user's actors
        const { actors } = await getUserActors();
        
        if (actors && actors.length > 0) {
          // Find the first active actor, or fallback to first actor
          const activeActorFromList = actors.find(a => a.isActive) || actors[0];
          
          console.log('[useActorSync] Synced activeActor:', activeActorFromList);
          console.log('[useActorSync] Available actors:', actors);
          
          // Update store with actors data
          setActiveActor(activeActorFromList);
          setAvailableActors(actors);
          
          hasSyncedRef.current = true;
        } else {
          console.warn('[useActorSync] No actors found for user');
        }
      } catch (error) {
        console.error('[useActorSync] Failed to sync activeActor:', error);
        // Don't throw - let user continue, they'll see the error on profile page
      }
    };

    syncActiveActor();
  }, [isAuthenticated, user, activeActor, setActiveActor, setAvailableActors]);
}
